/**
 * Body Fat % calculator. Multiple methods.
 * All measurements in metric (cm, kg).
 */

export type Gender = "male" | "female";
export type BFMethod = "navy" | "bmi" | "jp3";

export type BFCategoryKey =
  | "essential"
  | "athletes"
  | "fitness"
  | "average"
  | "obese";

export interface BFRanges {
  essential: [number, number];
  athletes: [number, number];
  fitness: [number, number];
  average: [number, number];
  obese: [number, number]; // [min, Infinity-ish]
}

export const BF_RANGES: Record<Gender, BFRanges> = {
  male: {
    essential: [2, 5],
    athletes: [6, 13],
    fitness: [14, 17],
    average: [18, 24],
    obese: [25, 100],
  },
  female: {
    essential: [10, 13],
    athletes: [14, 20],
    fitness: [21, 24],
    average: [25, 31],
    obese: [32, 100],
  },
};

export function categorize(bfPct: number, gender: Gender): BFCategoryKey {
  const r = BF_RANGES[gender];
  if (bfPct < r.essential[1]) return "essential";
  if (bfPct <= r.athletes[1]) return "athletes";
  if (bfPct <= r.fitness[1]) return "fitness";
  if (bfPct <= r.average[1]) return "average";
  return "obese";
}

const log10 = (n: number) => Math.log(n) / Math.LN10;

/* -------- US Navy Method -------- */

export interface NavyInput {
  gender: Gender;
  heightCm: number;
  waistCm: number;
  neckCm: number;
  hipCm?: number; // women only
}

export function navyBodyFat(inp: NavyInput): number {
  const { gender, heightCm, waistCm, neckCm, hipCm } = inp;
  if (gender === "male") {
    if (waistCm <= neckCm) return NaN;
    return 86.010 * log10(waistCm - neckCm) - 70.041 * log10(heightCm) + 36.76;
  }
  const hip = hipCm ?? 0;
  const denom = waistCm + hip - neckCm;
  if (denom <= 0) return NaN;
  return 163.205 * log10(denom) - 97.684 * log10(heightCm) - 78.387;
}

/* -------- Deurenberg (BMI-based) -------- */

export interface BMIInput {
  gender: Gender;
  age: number;
  heightCm: number;
  weightKg: number;
}

export function deurenbergBodyFat(inp: BMIInput): number {
  const { gender, age, heightCm, weightKg } = inp;
  const m = heightCm / 100;
  const bmi = weightKg / (m * m);
  const sex = gender === "male" ? 1 : 0;
  return 1.20 * bmi + 0.23 * age - 10.8 * sex - 5.4;
}

/** Deurenberg 2002: South Asians have ~3-5% higher BF at same BMI. */
export function southAsianAdjusted(bf: number, gender: Gender): number {
  return bf + (gender === "male" ? 3.5 : 5);
}

/* -------- Jackson-Pollock 3-site -------- */

export interface JP3Input {
  gender: Gender;
  age: number;
  /** men: chest + abdomen + thigh ; women: triceps + suprailiac + thigh */
  s1: number;
  s2: number;
  s3: number;
}

export function jp3BodyFat(inp: JP3Input): number {
  const { gender, age, s1, s2, s3 } = inp;
  const sum = s1 + s2 + s3;
  let density: number;
  if (gender === "male") {
    density = 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * age;
  } else {
    density = 1.0994921 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * age;
  }
  return 495 / density - 450;
}

/* -------- Lean / Fat mass -------- */

export interface MassBreakdown {
  fatKg: number;
  leanKg: number;
}

export function massBreakdown(weightKg: number, bfPct: number): MassBreakdown {
  const fatKg = (weightKg * bfPct) / 100;
  return { fatKg, leanKg: weightKg - fatKg };
}

/** Weight to lose to reach target BF%, holding lean mass constant. */
export function weightToTarget(weightKg: number, bfPct: number, targetPct: number): number {
  if (targetPct >= bfPct) return 0;
  const lean = weightKg * (1 - bfPct / 100);
  // newWeight = lean / (1 - targetPct/100)
  const newWeight = lean / (1 - targetPct / 100);
  return weightKg - newWeight;
}

/* -------- Validation -------- */

export type BFError =
  | "missing"
  | "height_invalid"
  | "weight_invalid"
  | "age_invalid"
  | "waist_invalid"
  | "neck_invalid"
  | "hip_invalid"
  | "skinfold_invalid";

export function validateNavy(inp: Partial<NavyInput>): { ok: true } | { ok: false; error: BFError } {
  const { gender, heightCm, waistCm, neckCm, hipCm } = inp;
  if (heightCm == null || waistCm == null || neckCm == null) return { ok: false, error: "missing" };
  if (!Number.isFinite(heightCm) || heightCm < 100 || heightCm > 230) return { ok: false, error: "height_invalid" };
  if (!Number.isFinite(waistCm) || waistCm < 40 || waistCm > 200) return { ok: false, error: "waist_invalid" };
  if (!Number.isFinite(neckCm) || neckCm < 20 || neckCm > 70) return { ok: false, error: "neck_invalid" };
  if (gender === "male" && waistCm <= neckCm) return { ok: false, error: "waist_invalid" };
  if (gender === "female") {
    if (hipCm == null || !Number.isFinite(hipCm) || hipCm < 50 || hipCm > 200) return { ok: false, error: "hip_invalid" };
    if (waistCm + hipCm - neckCm <= 0) return { ok: false, error: "waist_invalid" };
  }
  return { ok: true };
}

export function validateBMIMethod(inp: Partial<BMIInput>): { ok: true } | { ok: false; error: BFError } {
  const { age, heightCm, weightKg } = inp;
  if (age == null || heightCm == null || weightKg == null) return { ok: false, error: "missing" };
  if (!Number.isFinite(age) || age < 15 || age > 100) return { ok: false, error: "age_invalid" };
  if (!Number.isFinite(heightCm) || heightCm < 100 || heightCm > 230) return { ok: false, error: "height_invalid" };
  if (!Number.isFinite(weightKg) || weightKg < 25 || weightKg > 350) return { ok: false, error: "weight_invalid" };
  return { ok: true };
}

export function validateJP3(inp: Partial<JP3Input>): { ok: true } | { ok: false; error: BFError } {
  const { age, s1, s2, s3 } = inp;
  if (age == null || s1 == null || s2 == null || s3 == null) return { ok: false, error: "missing" };
  if (!Number.isFinite(age) || age < 15 || age > 100) return { ok: false, error: "age_invalid" };
  for (const s of [s1, s2, s3]) {
    if (!Number.isFinite(s) || s < 2 || s > 80) return { ok: false, error: "skinfold_invalid" };
  }
  return { ok: true };
}
